/* eslint no-param-reassign: 0 */
import {
  DirectoryGroupingConsts,
  ItemTableConsts,
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import {
  cloneDeep,
  get,
  isEmpty,
  merge,
} from 'lodash';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import CommandsApi from 'client/api/v2/CommandsApi';
import DeviceConstants from 'client/util/Constants/DeviceConstants';
import MdmMixin from 'client/store/mixins/MdmMixin';
import ModelMixin from 'client/store/modules/models/mixins/ModelMixin';
import PolicyApi from 'client/api/v2/PolicyApi';
import PolicySystemStatusApi from 'client/api/v2/PolicySystemStatusApi';
import SystemApi from 'client/api/v2/SystemApi';
import SystemInsightsConstants from 'client/util/Constants/SystemInsightsConstants';
import SystemMixin from 'client/store/mixins/SystemMixin';

const { groupTypes, memberTypes } = DirectoryGroupingConsts;
const { systemState } = SystemInsightsConstants;
const { linuxSshServerConfig } = DeviceConstants.filterSettingsPolicies;
const remoteAssistLinuxSupportedDistro = [
  'ubuntu-18.04-x86_64',
  'ubuntu-20.04-x86_64',
  'ubuntu-22.04-x86_64',
  'debian-11-x86_64',
  'rocky-8-x86_64',
  'linuxmint-20-x86_64',
  'linuxmint-21-x86_64',
];

export const getDefaultItem = () => ({
  _id: '',
  activationLock: {},
  active: false,
  agentVersion: '',
  allowMultiFactorAuthentication: false,
  allowPublicKeyAuthentication: false,
  allowSshPasswordAuthentication: false,
  allowSshRootLogin: false,
  arch: '',
  builtInCommands: [],
  connectionHistory: [],
  created: '',
  desktopCapable: false,
  description: '',
  deviceInformation: {},
  domainInfo: {},
  displayName: '',
  fde: {},
  hasServiceAccount: true,
  hostname: '',
  isProcessing: false,
  lastContact: '',
  modifySSHDConfig: false,
  networkInterfaces: [],
  organization: '',
  os: '',
  osFamily: '',
  remoteIP: '',
  sshRootEnabled: true,
  sshdParams: [],
  secureLoginEnabled: false,
  serialNumber: '',
  serviceAccountState: {
    passwordAPFSValid: true,
    passwordODValid: true,
    hasSecureToken: true,
  },
  systemInsights: { state: systemState.deferred },
  systemTimezone: '',
  systemToken: '',
  templateName: '',
  version: '',
  mdm: {},
  mdmDevices: [],
  allowSshRootLoginDisable: false,
  allowSshRootLoginPolicyId: '',
});

export default {
  namespaced: true,

  state: {
    item: getDefaultItem(),

    // Explicitly determine whether an item is being created or updated. This
    // is useful so that the UI can determine the _precise_ state of an item.
    itemState: {
      create: undefined,
      update: undefined,
    },

    // Keep track of initial item state so the Aside Panel can be "stateful"
    previousItem: getDefaultItem(),
  },

  actions: {
    ...ModelMixin.actions,

    async createMdmSettings({ commit, state }) {
      await MdmMixin.actions.createMdmSettings({ commit, state });
    },

    /**
     * @todo Consider moving this to the the ModelMixin. This should work in the
     * case where if the default functionality is sufficient, then there'd be no
     * need to provide a new implementation as seen here.
     *
     * Doing this would imply that the ModelMixin would be a function that
     * returned an object, so that the getDefaultItem() method could be injected
     * into the object by the caller. e.g.:
     *
     * import modelMixin from 'path/to/ModelMixin';
     *
     * const getDefaultItem = () => {
     *   // ...
     * };
     *
     * const defaultModel = modelMixin(getDefaultItem, '_id', SystemApi);
     *
     * export default {
     *   actions: {
     *     ...defaultModel.actions,
     *
     *     someNewAction() {},
     *   },
     *   getters: {
     *     ...defaultModel.getters,
     *
     *     someNewGetter() {},
     *   },
     *   mutations: {
     *     ....defaultModel.mutations,
     *
     *     SOME_NEW_MUTATION() {},
     *   },
     * };
     */

    async fetchActivationLockInformation({ commit, state }, payload = {}) {
      await MdmMixin.actions.fetchActivationLockInformation({ commit, state }, payload);
    },

    async fetchItem(context = { getters: { id: {} } }) {
      const { id } = context.getters;

      if (id) {
        return SystemApi
          .get(id)
          .then(async (item) => {
            let mdmDetails = {};
            // Clone the item so that any changes to non-primitive type values,
            // such as an array of phone numbers, are _not_ accidentally made
            // to the previous item when the item is mutated.
            if (item.osFamily === ItemTableConsts.parentOSType.ios
              || item.osFamily === ItemTableConsts.parentOSType.darwin) {
              try {
                await context.dispatch('fetchMdmSettings');
              } catch {
                context.dispatch(`${notificationModule}/triggerErrorNotification`, {
                  title: 'Error',
                  message: 'There was a problem fetching MDM settings.',
                }, { root: true });
              }
            }
            // this ios gate is important - see note below
            if (item.osFamily === ItemTableConsts.parentOSType.ios) {
              try {
                mdmDetails = await MdmMixin.actions
                  .fetchMdmDeviceDetails(context.state, item.mdm.internal.deviceId);
              } catch {
                context.dispatch(`${notificationModule}/triggerErrorNotification`, {
                  title: 'Error',
                  message: 'There was a problem fetching MDM device details.',
                }, { root: true });
              }
              /**
               * On macs we have an agent running that populates a lot of the
               * system data. There is no agent on iOS devices so the
               * item = merge(item, mdmDetails) line isn't overwriting things
               * on iOS devices because MDM is the source of truth. Do NOT call
               * this on mac devices as the merge will overwrite important data!
               */
              item = merge(item, mdmDetails);
            }

            if (item.osFamily === ItemTableConsts.parentOSType.linux) {
              const policiesTemplatesResult = await PolicyApi.getOsPoliciesCount(
                ItemTableConsts.parentOSType.linux,
              );
              const policiesTemplates = policiesTemplatesResult.results;
              const policies = await PolicySystemStatusApi.getAttachedPolicies(item.id);

              if (policies.length > 0) {
                const mapPolicies = policies.map(policy => {
                  const match = policiesTemplates.find(
                    template => template.id === policy.id,
                  );
                  if (typeof match !== typeof undefined) {
                    policy.template = match.template;
                  }
                  return policy;
                });

                const matchPolicy = mapPolicies.find(
                  policy => policy.template && (policy.template.name === linuxSshServerConfig),
                );

                if (matchPolicy) {
                  item.allowSshRootLoginDisable = true;
                  item.allowSshRootLoginPolicyId = matchPolicy.id;
                }
              }
            }

            context.commit('SET_ITEM', cloneDeep(item));
            context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));
          });
      }

      return Promise.resolve();
    },

    async fetchMdmSettings({ commit }) {
      await MdmMixin.actions.fetchMdmSettings({ commit });
    },

    saveItem(context) {
      const { getters } = context;

      const handleSaveItem = (item) => {
        context.commit('SET_ITEM', cloneDeep(item));
        context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));
        context.commit('SET_ITEM_STATE', { update: true });

        // Ensure that the updated item is updated in the Systems Main Panel by
        // passing the item to the SystemCollection so it can be synced in the store
        // immediately.
        context.dispatch('SystemCollection/refreshCollection', { item }, { root: true });
        context.dispatch('SystemUnacknowledgedCollection/refreshCollection', { item }, { root: true });
        return item;
      };

      const itemToSave = { ...getters.item };

      if (getters.haveAssociationsChanged && itemToSave.acknowledged === false) {
        itemToSave.acknowledged = true;
      }

      return SystemApi.update(itemToSave).then(handleSaveItem);
    },

    bindPolicyToSystem(context) {
      return context.dispatch('bindAssociations', {
        parentType: memberTypes.system,
        targetType: memberTypes.policy,
      });
    },

    bindSystemToSystemGroups(context) {
      return context.dispatch('bindAssociations', {
        parentType: memberTypes.system,
        targetType: groupTypes.systemGroup,
      });
    },

    bindSystemToUsers(context) {
      return context.dispatch('bindAssociations', {
        parentType: memberTypes.system,
        targetType: memberTypes.user,
      });
    },

    bindSudoStatus(context) {
      const { id } = context.getters;
      const targetsToUpdate = context.getters.getSudoersToUpdate;

      const payload = {
        parentType: memberTypes.system,
        parentId: id,
        targetType: memberTypes.user,
        targetsToUpdate,
      };

      return context.dispatch(
        'DirectoryGroupingModule/bindAttributes',
        payload,
        { root: true },
      );
    },

    resetItem({ commit }) {
      commit('RESET_ITEM');
      commit('SystemInsightsModule/RESET_STATE', null, { root: true });
    },

    runAgentLogCommand(context, payload = {}) {
      let queuedCommandInterval;
      let queueCheckCounter = 0;
      const { getters } = context;
      const { timeout = 5000 } = payload;
      const { id } = getters;

      return new Promise((resolve, reject) => {
        SystemApi.runAgentLogCommand(id)
          .then((runResp) => {
            clearInterval(queuedCommandInterval);
            const { commandId } = runResp;

            queuedCommandInterval = setInterval(() => {
              CommandsApi.getMostRecentResult(commandId)
                .then((commandResult) => {
                  if (commandResult && commandResult.response) {
                    clearInterval(queuedCommandInterval);
                    resolve(commandResult);
                  }

                  // wait 3 minutes before failing
                  if (queueCheckCounter === 36) {
                    clearInterval(queuedCommandInterval);
                    reject(new Error('There was a problem getting agent log results.'));
                  }

                  queueCheckCounter += 1;
                });
            }, timeout);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
  },

  getters: {
    ...ModelMixin.getters,

    active(state = { item: {} }) {
      if (state.item.active) {
        return 'Active, System Reporting';
      }

      return 'Inactive, System Not Reporting';
    },

    isActive(state = { item: {} }) {
      return state.item.active;
    },

    displayName(state = { item: {} }) {
      const { displayName, hostname } = state.item;
      return displayName || hostname || undefined;
    },

    isDesktopCapable(state = { item: {} }) {
      const { desktopCapable } = state.item;
      return desktopCapable;
    },

    isRemoteAssistLinuxSupportedDistro(state = { item: {} }) {
      const { templateName } = state.item;
      return remoteAssistLinuxSupportedDistro.includes(templateName);
    },

    fdeDescriptionTerse(state = { item: {} }) {
      const fde = state.item.fde || {};
      if (fde.active) {
        return 'Yes';
      }
      if (fde.cloudEncryptionProvider) {
        return `See ${fde.cloudEncryptionProvider} console`;
      }
      return 'No';
    },

    fdeDescriptionVerbose(state = { item: {} }) {
      const fde = state.item.fde || {};
      if (fde.active) {
        return 'Encrypted';
      }
      if (fde.cloudEncryptionProvider) {
        return `See your ${fde.cloudEncryptionProvider} console to verify`;
      }
      return 'Unencrypted';
    },

    getBuiltInCommands(state = { item: {} }) {
      return state.item.builtInCommands || [];
    },

    getEnrollmentType(state = { item: {} }) {
      // falsey values will come back from the API as 'unknown' - we want 'not available' in the UI
      if (!state.item.mdm.enrollmentType || state.item.mdm.enrollmentType === 'unknown') {
        return 'not available';
      }
      return state.item.mdm.enrollmentType;
    },

    getSudoersToUpdate(state, getters, rootState, rootGetters) {
      const module = StoreModuleMap[ItemTypeConsts.systemUser];
      return rootGetters[`${module}/getSudoersToUpdate`];
    },

    getServiceAccountState(state = { item: {} }) {
      return SystemMixin.getServiceAccountState(state.item);
    },

    getSystemNIC(state = { item: {} }) {
      return SystemMixin.getSystemNIC(state.item);
    },

    getProcessingData(state = { item: {} }) {
      return state.item.getProcessingData();
    },

    getUserAssociationChanges(state, getters) {
      const usersToBind = getters.getTargetsToBind(memberTypes.user);
      const usersToUnbind = getters.getTargetsToUnbind(memberTypes.user);

      return { binds: usersToBind, unbinds: usersToUnbind };
    },

    haveAssociationsChanged(state, getters) {
      const systemGroupsToBind = getters.getTargetsToBind(groupTypes.systemGroup);
      const systemGroupsToUnbind = getters.getTargetsToUnbind(groupTypes.systemGroup);
      const usersToBind = getters.getTargetsToBind(memberTypes.user);
      const usersToUnbind = getters.getTargetsToUnbind(memberTypes.user);
      const sudoersToUpdate = getters.getSudoersToUpdate;

      if (systemGroupsToBind.length
          || systemGroupsToUnbind.length
          || usersToBind.length
          || usersToUnbind.length
          || Object.keys(sudoersToUpdate).length) {
        return true;
      }

      return false;
    },

    /**
     * Legacy items that reside in Mongo use the _id field while newer items
     * that live in Postgres, such as Policies and Groups, use the id field.
     */
    idField() {
      return '_id';
    },

    isFdeKeyPresent(state = { item: {} }) {
      return SystemMixin.isFdeKeyPresent(state.item);
    },

    isInBadState(state = { item: {} }) {
      return SystemMixin.isInBadState(state.item);
    },

    hasBadServiceAccountState(state = { item: {} }) {
      return SystemMixin.hasBadServiceAccountState(state.item);
    },

    isIOS(state = { item: {} }) {
      return SystemMixin.isIOS(state.item);
    },

    isLinux(state = { item: {} }) {
      return SystemMixin.isLinux(state.item);
    },

    isMacOS(state = { item: {} }) {
      return SystemMixin.isMacOS(state.item);
    },

    isAzureDomainJoined(state = { item: {} }) {
      return SystemMixin.isAzureDomainJoined(state.item);
    },

    isWindowsDomainJoined(state = { item: {} }) {
      return SystemMixin.isWindowsDeviceDomainJoined(state.item);
    },

    isProcessing(state = { item: {} }) {
      return state.item.isProcessing;
    },

    isSystemMdmEnrolled(state = { item: {} }) {
      return SystemMixin.isSystemMdmEnrolled(state.item);
    },

    isSystemMdmVendorValid(state = { item: {} }) {
      return SystemMixin.isSystemMdmVendorValid(state.item);
    },

    isSystemDepEnrolled(state = { item: {} }) {
      return SystemMixin.isSystemDepEnrolled(state.item);
    },

    isSystemEnrolledWithDifferentVendor(state = { item: {} }) {
      return SystemMixin.isSystemEnrolledWithDifferentVendor(state.item);
    },

    isMultifactorSupported(state = { item: {} }) {
      return SystemMixin.isMultifactorSupported(state.item);
    },

    isUserApproved(state = { item: {} }) {
      return SystemMixin.isUserApproved(state.item);
    },

    isSecureLoginEnabled(state = { item: {} }) {
      return state.item.secureLoginEnabled;
    },

    isSystemInsightsSupported(state = { item: {} }) {
      const { item } = state;
      return !!(item.systemInsights && String(item.arch).includes('64'));
    },

    isWindows(state = { item: {} }) {
      return SystemMixin.isWindows(state.item);
    },

    osWithVersion(state = { item: {} }) {
      const { os, osVersionDetail, version } = state.item;
      const patch = get(osVersionDetail, 'patch');
      const revision = get(osVersionDetail, 'revision');
      const build = SystemMixin.isWindows(state.item) && patch && revision ? `${patch}.${revision}` : revision;

      if (os || version) {
        const versionString = `${os || ''} ${version || ''} ${build ? `(${build})` : ''}`;
        const versionStringFormatted = versionString.replace(/\s+/g, ' ').trim();
        return versionStringFormatted;
      }

      return '';
    },

    showSystemInsights(state = { item: {} }, getters, rootState) {
      const enableSystemInsights = rootState.SettingsModule.features.systemInsights.enabled;
      return SystemMixin.showSystemInsights(state.previousItem, enableSystemInsights);
    },

    getMdmDevices(state = { item: {} }) {
      const { mdmDevices } = state.item;
      return mdmDevices;
    },

    getEmmDeviceId(state = { item: {} }) {
      return state.item.mdm.internal.deviceId;
    },

    getSystemMdmId(state = { item: {} }) {
      return SystemMixin.getSystemMdmId(state.item);
    },

    getSystemMdmProfileIdentifier(state = { item: {} }) {
      return SystemMixin.getSystemMdmProfileIdentifier(state.item);
    },

    isOrgMdmEnrolled(state = { item: {} }) {
      const { mdmDevices } = state.item;
      if (isEmpty(mdmDevices)) return false;
      if (!mdmDevices[0].apnsPushTopic) return false;
      if (mdmDevices[0].apnsPushTopic === '') return false;
      return true;
    },

    secureTokenAdminList(state = { item: {} }) {
      const { userMetrics } = state.item;
      const secureTokenAdminList = [];
      if (userMetrics) {
        userMetrics.forEach((user) => {
          if (user.secureTokenEnabled && user.admin) {
            secureTokenAdminList.push(user);
          }
        });
      }
      return secureTokenAdminList;
    },

    isAndroid(state = { item: {} }) {
      return SystemMixin.isAndroid(state.item);
    },
  },

  mutations: {
    ...ModelMixin.mutations,

    /**
     * @todo Consider moving this to the ModelMixin as discussed above!
     */
    RESET_ITEM(state = {
      item: {},
      itemState: {},
    }) {
      state.item = getDefaultItem();
      state.itemState.create = undefined;
      state.itemState.update = undefined;
      state.previousItem = getDefaultItem();
    },
  },
};
